import { createSelector } from "reselect";
import { GEOGRAPHY_LEVELS, OFFER_PRICE_TYPES, PRODUCTS_FILTERS_KEY } from "app/constants";
import { sortBy, includes, uniq, flatten, pick, compact, forOwn } from "lodash";
import { getDayCount, sortProducts } from "app/utils/utils";
import { FacetedClassification, FacetedQuery } from "immfacet";
import { fromJS } from "immutable";
import { computeDestinationFilterValues } from "app/utils/productFilterUtils";

export const getCurrentLandingPage = state => state?.landingPage?.current;
const getActiveFilter = state => state?.landingPage?.activeFilter;
const getLandingPageProducts = state => state?.landingPage?.products;
const getLandingPageCatalogProducts = state => state?.landingPage?.catalogProducts;
const getProductsFilters = state => state?.productsFilters;
export const getLandingPageMeta = state => state?.landingPage?.current?.meta;

export const getFlashsaleProductsOfLandingPage = createSelector(
	[getLandingPageProducts, getLandingPageCatalogProducts],
	(products = [], catalogProducts = []) => {
		const allProducts = products?.concat(catalogProducts);
		const flashsaleProducts = allProducts?.filter(product => {
			return product.isFlashsale === true;
		});
		return flashsaleProducts;
	}
);

export const getCatalogProductsOfLandingPage = createSelector(
	[getLandingPageProducts, getLandingPageCatalogProducts],
	(products = [], catalogProducts = []) => {
		const allProducts = products?.concat(catalogProducts);
		const nonFlashsaleProducts = allProducts?.filter(product => {
			return product.isFlashsale === false;
		});
		return nonFlashsaleProducts;
	}
);

export const getProductsOfLandingPage = createSelector(
	[getCatalogProductsOfLandingPage],
	(catalogProducts = []) => {
		return catalogProducts;
	}
);

const getLandingPageProductsFacetQuery = createSelector(
	[getProductsOfLandingPage, getActiveFilter, getProductsFilters],
	(products = [], activeFilter, productsFilters = {}) => {
		let facetCollection = new FacetedClassification(fromJS(products));
		const countryDestinationsList = sortBy(
			uniq(flatten(products?.map(({ country }) => country || [])))
		);
		const levelToCheck =
			countryDestinationsList?.length <= 1
				? GEOGRAPHY_LEVELS.RESORT
				: GEOGRAPHY_LEVELS.COUNTRY;

		facetCollection = facetCollection
			.addFacet(
				PRODUCTS_FILTERS_KEY.TOPIC,
				product => {
					if (!product.get("topics")) {
						return undefined;
					}
					if (
						activeFilter &&
						(!product.get("merchandisingTopics") ||
							!includes(product.get("merchandisingTopics").toJS(), activeFilter))
					) {
						return undefined;
					}
					return product.get("topics").toJS();
				},
				{
					multiValue: true,
				}
			)
			.addFieldFacet(PRODUCTS_FILTERS_KEY.MERCHANDISINGS, {
				multiValue: true,
			})
			.addFacet(PRODUCTS_FILTERS_KEY.DESTINATION, product => {
				if (!product.get(levelToCheck)) {
					return undefined;
				}

				if (
					activeFilter &&
					(!product.get("merchandisingTopics") ||
						!includes(product.get("merchandisingTopics").toJS(), activeFilter))
				) {
					return undefined;
				}

				return product.get(levelToCheck);
			})
			.addFacet(
				PRODUCTS_FILTERS_KEY.ZONE,
				product => {
					if (!product.get("zones")) {
						return undefined;
					}
					if (
						activeFilter &&
						(!product.get("merchandisingTopics") ||
							!includes(product.get("merchandisingTopics").toJS(), activeFilter))
					) {
						return undefined;
					}
					return product
						.get("zones")
						.toJS()
						.map(zone => {
							return zone;
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.CATEGORY,
				product => {
					if (!product.get("merchandisingTopics")) {
						return undefined;
					}
					if (
						activeFilter &&
						(!product.get("merchandisingTopics") ||
							!includes(product.get("merchandisingTopics").toJS(), activeFilter))
					) {
						return undefined;
					}
					return product.get("merchandisingTopics").toJS();
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.BADGE,
				product => {
					if (!product.get("badges")) {
						return undefined;
					}
					if (
						activeFilter &&
						(!product.get("merchandisingTopics") ||
							!includes(product.get("merchandisingTopics").toJS(), activeFilter))
					) {
						return undefined;
					}
					return product
						.get("badges")
						.toJS()
						.map(badge => {
							return badge.label;
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(
				PRODUCTS_FILTERS_KEY.MONTH,
				product => {
					if (!product.get("months")) {
						return undefined;
					}
					if (
						activeFilter &&
						(!product.get("merchandisingTopics") ||
							!includes(product.get("merchandisingTopics").toJS(), activeFilter))
					) {
						return undefined;
					}
					return product
						.get("months")
						.toJS()
						.map(month => {
							return String(month.startAt);
						});
				},
				{
					multiValue: true,
				}
			)
			.addFacet(PRODUCTS_FILTERS_KEY.START_AT, product => {
				if (!product.get("startAt")) {
					return undefined;
				}
				if (
					activeFilter &&
					(!product.get("merchandisingTopics") ||
						!includes(product.get("merchandisingTopics").toJS(), activeFilter))
				) {
					return undefined;
				}
				return String(
					getDayCount(product.get("startAt"), Date.now()) <= 2 &&
						getDayCount(product.get("startAt"), Date.now()) >= 0
				);
			})
			.addFacet(PRODUCTS_FILTERS_KEY.DISCOUNT_PERCENTAGE, product => {
				if (!product.get("fromPriceType")) {
					return undefined;
				}
				if (
					activeFilter &&
					(!product.get("merchandisingTopics") ||
						!includes(product.get("merchandisingTopics").toJS(), activeFilter))
				) {
					return undefined;
				}

				return String(
					product.get("fromPriceType").toJS().type ===
						OFFER_PRICE_TYPES.FROM_PRICE_TYPE_SAVE_UP_TO
						? Number(product.get("fromPriceType").toJS().value) >= 50
						: undefined
				);
			});

		let facetQuery = new FacetedQuery(facetCollection);

		forOwn(productsFilters, (value, key) => {
			// on doit gérer les valeurs en string et boolean (pour startAt et endAt)
			if ((value && value.length > 0) || (value && typeof value === "boolean")) {
				facetQuery = facetQuery.select({
					name: key,
					values:
						typeof value === "string" || typeof value === "boolean" ? [value] : value,
				});
			}
		});

		return facetQuery;
	}
);

export const getLandingPageSelectedFacets = createSelector(
	[getLandingPageProductsFacetQuery],
	(facetQuery = {}) => {
		return facetQuery.selectedFacets().toJS() || [];
	}
);

export const getFilteredProductsOfLandingPage = createSelector(
	[getActiveFilter, getLandingPageProductsFacetQuery],
	(activeFilter, facetQuery) => {
		const productsFiltered = sortProducts({ products: facetQuery.selectedItems().toJS() });

		if (activeFilter) {
			return productsFiltered.filter(product => {
				return includes(product.merchandisingTopics, activeFilter);
			});
		}
		return productsFiltered;
	}
);

export const getVisibleProductsOfLandingPage = createSelector(
	[getFilteredProductsOfLandingPage],
	(products = []) => {
		return products;
	}
);

export const getLandingPageVisibleFilters = createSelector(
	[getLandingPageProductsFacetQuery],
	(facetQuery = {}) => {
		return facetQuery.selectedFacetValues().toJS() || [];
	}
);

export const getLandingPageDestinationFilterValues = createSelector(
	[getVisibleProductsOfLandingPage],
	computeDestinationFilterValues
);

export const shouldDisplayDestinationFilter = createSelector(
	[getProductsOfLandingPage],
	products => {
		const filterValues = computeDestinationFilterValues(products);
		return filterValues?.length > 1;
	}
);

export const getLandingPageTopicFilterValues = createSelector(
	[getVisibleProductsOfLandingPage],
	(products = []) => {
		if (!products || products.length === 0) {
			return [];
		}

		const topics = products.map(product => {
			return product.topics || [];
		});

		return sortBy(uniq(flatten(topics)));
	}
);

export const getLandingPagePeriodFilterValues = createSelector(
	[getVisibleProductsOfLandingPage],
	(products = []) => {
		const periods = products.map(product => {
			if (!product.periods || product.periods.length === 0) {
				return [];
			}
			return product.periods || [];
		});

		const sortedPeriods = sortBy(flatten(periods), "startAt");

		const labels = sortedPeriods.map(period => {
			return pick(period, "label").label || [];
		});

		return uniq(labels);
	}
);

export const getLandingPageMonthFilterValues = createSelector(
	[getVisibleProductsOfLandingPage],
	(products = []) => {
		const months = products.map(product => {
			if (!product.months || product.months.length === 0) {
				return [];
			}
			return product.months || [];
		});

		const sortedMonths = sortBy(flatten(months), "startAt");

		const labels = sortedMonths.map(month => {
			return String(pick(month, "startAt").startAt) || [];
		});

		return uniq(labels);
	}
);

export const getLandingPageBadgeFilterValues = createSelector(
	[getVisibleProductsOfLandingPage],
	(products = []) => {
		const badges = products.map(product => {
			if (!product.badges || product.badges.length === 0) {
				return [];
			}
			return product.badges || [];
		});

		const firstBadges = badges.map(badgeList => {
			return badgeList[0];
		});

		const sortedBadges = sortBy(flatten(compact(firstBadges)), "label");

		const badgeLabels = sortedBadges.map(badge => {
			return pick(badge, "label").label || [];
		});

		return uniq(badgeLabels);
	}
);

export const getLandingPageTopicsFilterValuesFromLandingPageCode = createSelector(
	[getCurrentLandingPage],
	(landingPage = {}) => {
		let merchandisingTopics = [];

		if (landingPage.merchandisingTopics) {
			merchandisingTopics = landingPage.merchandisingTopics.reduce(
				(filterValues, { filterValue: currentFilterValue }) => {
					return currentFilterValue
						? [...filterValues, currentFilterValue]
						: filterValues;
				},
				[]
			);
		}

		return merchandisingTopics;
	}
);

export const getLandingPageMedias = createSelector(
	[getCurrentLandingPage],
	(landingPage = {}) => {
		return landingPage?.media?.articles || [];
	}
);

// TODO temporaire (voir FBR-2686)
export const shouldLandingPageHideProductPriceBanner = createSelector(
	[getCurrentLandingPage],
	(landingPage = {}) => {
		return !landingPage?.topicFilterValues?.some(topic =>
			["Circuit", "Autotour", "Croisière", "Combiné"].includes(topic)
		);
	}
);
